/**
 * Try your agent: a drawer at the right edge, over Settings, with a call in it.
 *
 * A DRAWER RATHER THAN THE DIALOG IT REPLACES, because Settings v2 draws it that way and
 * because the thing somebody does after a test call is change a setting and call again.
 * The page stays visible behind the scrim, one click away, and the drawer's own header says
 * how many test calls are left this hour - the Worker's limit, counted by the Worker.
 *
 * IT USES WHAT IS SAVED, AND SAYS SO WHEN THAT IS NOT WHAT IS ON SCREEN. The hosted agent
 * reads its setup from the Worker when the call starts, so an unsaved brief is not in it.
 * With a draft open the idle state adds one warm line, rather than letting somebody test a
 * change that is not there and conclude it did nothing.
 *
 * ITS SHADOW AND ITS ACCENT BUTTON ARE THE TWO EXCEPTIONS `styles.css` NAMES. Everything
 * else here is the product's: the scrim is `Dialog`'s, the escape hatches are
 * `useModalLayer`'s, and closing hangs up.
 */

import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Alert, Close } from "../Icons";
import { useModalLayer } from "../useModalLayer";
import { useScopedTo } from "../Shell";
import { Button, IconButton, cx } from "../../ui";
import { useTestCall, withTestCalls } from "./useTestCall";

/** The prototype's phone glyph, filled. Icons.tsx has a microphone and no handset. */
export function Handset({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.6a1 1 0 0 1-.25 1z" />
    </svg>
  );
}

export function TryItDrawer({
  agentId,
  agentName,
  remaining: initialRemaining,
  perHour,
  unsaved,
  onClose,
}: {
  agentId: string;
  agentName: string;
  remaining?: number;
  perHour?: number;
  /** A draft is open on the page, which this call will not hear. */
  unsaved: boolean;
  onClose: () => void;
}) {
  const [remaining, setRemaining] = useState(initialRemaining);
  const started = useCallback((left: number | undefined) => {
    setRemaining((current) => left ?? (current === undefined ? current : Math.max(0, current - 1)));
  }, []);
  const { phase, lines, speaking, error, callRow, clock, audio, call, end } = useTestCall(agentId, started);
  const panel = useRef<HTMLElement>(null);
  const to = useScopedTo();
  useModalLayer(panel, onClose);

  const spent = remaining !== undefined && remaining <= 0;
  const shown = lines.filter((line) => line.text);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50 bg-[color-mix(in_srgb,var(--color-ink)_30%,transparent)]"
        onMouseDown={onClose}
      />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Try your agent"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 flex w-[min(420px,100vw)] flex-col bg-paper shadow-float outline-none"
      >
        <div className="flex items-center justify-between gap-3 border-b border-line-soft px-5.5 py-5">
          <div className="grid gap-0.5">
            <span className="text-lg font-semibold text-ink">Try your agent</span>
            {remaining !== undefined && perHour !== undefined && (
              <span className="nums text-xs text-ink-faint">
                {remaining} of {perHour} test calls left this hour
              </span>
            )}
          </div>
          <IconButton type="button" onClick={onClose} className="size-8 bg-line-soft">
            <Close size={14} />
            <span className="sr-only">Close</span>
          </IconButton>
        </div>

        {/* Never unmounted during a call: the agent's voice plays through it. */}
        <audio ref={audio} autoPlay className="hidden" />

        {phase === "ready" ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <button
              type="button"
              aria-label="Start a test call"
              disabled={spent}
              onClick={() => void call()}
              className="grid size-19 cursor-pointer place-items-center rounded-full bg-accent text-accent-ink transition-colors hover:bg-accent-hover disabled:cursor-default disabled:opacity-55"
            >
              <Handset size={30} />
            </button>
            <span className="text-base font-semibold text-ink">Start a test call</span>
            <span className="max-w-[300px] text-sm text-ink-soft">
              Talk through your browser microphone. It uses your saved settings, so save first if you've changed anything.
            </span>
            {unsaved && (
              <span className="rounded-sm border border-warm-line bg-warm-soft px-3 py-2 text-sm text-ink">
                You have unsaved changes. This call uses what's saved.
              </span>
            )}
            {spent && <span className="text-sm text-ink-faint">No test calls left this hour. Try again later.</span>}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3.5 border-b border-line-soft bg-ground px-5.5 py-4">
              <span
                aria-hidden="true"
                className={cx("size-3 flex-none rounded-full", phase === "live" ? "live-dot bg-accent" : "bg-ink-faint")}
              />
              <span className="flex-1 text-sm font-medium text-ink" aria-live="polite">
                {phase === "starting" && "Calling… the first call after a quiet spell can take a few seconds."}
                {phase === "live" && (speaking ? `${agentName} is speaking` : "On a call with your agent")}
                {phase === "ended" && "Call ended"}
              </span>
              <span className="nums font-mono text-sm text-ink-soft">{clock}</span>
            </div>

            <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-5.5 py-4.5">
              {shown.map((line, index) => (
                <div key={index} className={cx("flex flex-col gap-1", line.who === "caller" ? "items-end" : "items-start")}>
                  <span className="text-2xs font-semibold tracking-[0.1em] text-ink-faint uppercase">
                    {line.who === "caller" ? "You" : "Agent"}
                  </span>
                  <span
                    className={cx(
                      "max-w-[300px] rounded-md px-3.5 py-2.5 text-sm",
                      line.who === "caller" ? "bg-ink text-paper" : "bg-sunken text-ink",
                    )}
                  >
                    {line.text}
                  </span>
                </div>
              ))}
              {error && (
                <p role="alert" className="flex gap-2 rounded-sm border border-alert-line bg-alert-soft px-3 py-2.5 text-sm text-ink">
                  <Alert size={16} className="mt-px flex-none text-alert" />
                  <span>{error}</span>
                </p>
              )}
            </div>

            <div className="flex gap-2.5 border-t border-line-soft px-5.5 py-4">
              {phase === "ended" ? (
                <>
                  {callRow !== null ? (
                    <Link
                      to={withTestCalls(to(`/calls/${callRow}`))}
                      className="grid h-11 flex-1 place-items-center rounded-sm bg-ink text-base font-medium text-paper hover:bg-ink-soft"
                    >
                      Open this call's record
                    </Link>
                  ) : (
                    <span className="grid h-11 flex-1 place-items-center text-sm text-ink-faint">Filing the call…</span>
                  )}
                  <Button type="button" className="h-11" disabled={spent} onClick={() => void call()}>
                    Call again
                  </Button>
                </>
              ) : (
                <Button type="button" variant="danger" className="h-11 flex-1 justify-center" onClick={() => end(null)}>
                  End call
                </Button>
              )}
            </div>
          </>
        )}
      </aside>
    </>,
    document.body,
  );
}
